/**
 * Shared Profile editor — works for owner_builder, architect, city_staff,
 * and platform_admin. Each role gets its own route entry, but the page
 * itself is the same. Per-role copy is driven by the `profileCopy` map.
 *
 * Uses /api/portal/profile?user_id=… (GET, PATCH).
 */
import React, { useEffect, useRef, useState } from "react";
import { useRole } from "../context/RoleContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, ImageUp } from "lucide-react";

const COPY = {
  owner_builder: {
    eyebrow: "Account",
    title: "Your profile.",
    dek: "How you appear to architects, city staff, and the platform. Notification preferences control how often we reach out.",
    bioPlaceholder:
      "Two or three sentences architects will see when you request a consultation.",
    roleLabel: "Owner / Builder",
    bell: "How we reach out.",
    bellDek:
      "Toggling off does not stop notifications from appearing in the in-app inbox — only how we deliver them by email.",
  },
  architect: {
    eyebrow: "Account",
    title: "Your account.",
    dek: "Personal account settings. Your firm-level profile (philosophy, awards, completed projects) lives under Firm Profile.",
    bioPlaceholder: "How you'd like to be introduced to potential clients.",
    roleLabel: "Architect",
    bell: "Notifications.",
    bellDek:
      "Email digests, project updates, and platform announcements.",
  },
  city_staff: {
    eyebrow: "Account",
    title: "Your DMD account.",
    dek: "Personal contact details used by the platform. Updates here do not change your Department of Metropolitan Development record.",
    bioPlaceholder:
      "Title and remit — useful for residents and architects who reach you through the platform.",
    roleLabel: "City Staff",
    bell: "Inbox preferences.",
    bellDek:
      "How the platform notifies you of new consultations, code-change reviews, and stage transitions.",
  },
  platform_admin: {
    eyebrow: "Account",
    title: "Platform admin account.",
    dek: "Your platform-admin identity. Changes are logged in the platform audit trail.",
    bioPlaceholder:
      "Short bio displayed alongside your administrative actions.",
    roleLabel: "Platform Admin",
    bell: "Administrative notifications.",
    bellDek:
      "Application backlogs, billing escalations, and critical system events.",
  },
};

const NOTIF_OPTIONS_BY_ROLE = {
  owner_builder: [
    ["email_digest", "Weekly digest email",
      "A Friday summary of project activity, replies, and saved designs."],
    ["project_updates", "Project updates",
      "Stage transitions, inspections logged, plan approvals, and document delivery."],
    ["consultation_replies", "Consultation replies",
      "Architect messages, follow-ups, and scheduling changes."],
    ["marketing", "Programme news",
      "New designs added to the registry, LISC partner spotlights, and code-change announcements."],
  ],
  architect: [
    ["email_digest", "Weekly digest email",
      "Monday summary of new leads, scheduled consultations, and platform metrics."],
    ["project_updates", "Project updates",
      "Stage transitions, document approvals, and inspections on your projects."],
    ["consultation_replies", "Consultation requests",
      "New consultation requests, replies, and scheduling changes."],
    ["marketing", "Programme news",
      "Platform announcements, code-change reviews, and LISC partner news."],
  ],
  city_staff: [
    ["email_digest", "Daily digest",
      "Morning summary of new applications, code reviews, and pending stage approvals."],
    ["project_updates", "Stage transitions",
      "Every project that crosses a stage you'd routinely review."],
    ["consultation_replies", "Inspections",
      "When an inspection is logged or a code change needs your sign-off."],
    ["marketing", "Programme news",
      "Civic partner updates, architect roster changes, and platform notices."],
  ],
  platform_admin: [
    ["email_digest", "Daily admin digest",
      "Morning summary of applications, billing, and audit-trail anomalies."],
    ["project_updates", "Critical events",
      "Failed payments, deactivated accounts, and onboarding errors."],
    ["consultation_replies", "User reports",
      "Owner / architect support escalations and abuse reports."],
    ["marketing", "Platform announcements",
      "Roadmap milestones, partner agreements, and feature releases."],
  ],
};

export default function ProfilePage({ role }) {
  const { user, refreshUser } = useRole();
  const effectiveRole = role || user?.role || "owner_builder";
  const copy = COPY[effectiveRole] || COPY.owner_builder;
  const notifOptions =
    NOTIF_OPTIONS_BY_ROLE[effectiveRole] || NOTIF_OPTIONS_BY_ROLE.owner_builder;

  const [profile, setProfile] = useState(null);
  const [draft, setDraft] = useState(null);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!user?.id) return;
    api
      .get(`/portal/profile?user_id=${user.id}`)
      .then((r) => {
        setProfile(r.data);
        setDraft(r.data);
      })
      .catch((e) => setErr(e));
  }, [user?.id]);

  if (err) {
    return (
      <div data-testid="profile-error" style={{ color: "#A23B2E" }}>
        Could not load your profile.
      </div>
    );
  }
  if (!profile || !draft) {
    return (
      <div style={{ color: "#6C6B85", fontStyle: "italic" }}>
        Loading profile…
      </div>
    );
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(profile);

  const update = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const updatePref = (k, v) =>
    setDraft((d) => ({
      ...d,
      notification_prefs: { ...(d.notification_prefs || {}), [k]: v },
    }));

  const initialsFromName = (name) =>
    (name || "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0])
      .join("")
      .toUpperCase();

  const handleAvatarPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setAvatarPreview(ev.target.result);
      update("avatar_initials", initialsFromName(draft.name));
      toast.success("Avatar preview set. Upload completes when you save.");
    };
    reader.readAsDataURL(f);
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: draft.name,
        email: draft.email,
        phone: draft.phone,
        organization: draft.organization,
        neighborhood: draft.neighborhood,
        title: draft.title,
        bio: draft.bio,
        avatar_initials: draft.avatar_initials || initialsFromName(draft.name),
        notification_prefs: draft.notification_prefs,
      };
      await api.patch(`/portal/profile?user_id=${user.id}`, payload);
      setProfile({ ...profile, ...payload });
      setDraft({ ...draft, ...payload });
      if (refreshUser) await refreshUser();
      toast.success("Profile saved.");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not save profile. Try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const initials =
    draft.avatar_initials || initialsFromName(draft.name) || "??";

  const isCityOrAdmin =
    effectiveRole === "city_staff" || effectiveRole === "platform_admin";

  return (
    <section className="animate-fade-in-up" data-testid="profile-page">
      {/* Hero */}
      <header
        className="grid lg:grid-cols-[1fr_auto] gap-10 items-end"
        style={{ paddingBottom: 28 }}
      >
        <div>
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 28,
                height: 2,
                background: "#B14E2F",
              }}
            />
            <span className="wn-label" style={{ color: "#8F3C23" }}>
              {copy.eyebrow}
            </span>
          </div>
          <h1
            className="mt-4"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(36px, 4.6vw, 52px)",
              fontWeight: 500,
              lineHeight: 1.06,
              letterSpacing: "-0.01em",
              color: "#1B1F4B",
              margin: 0,
            }}
          >
            {copy.title}
          </h1>
          <p
            className="mt-4"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 18,
              lineHeight: 1.55,
              maxWidth: 640,
              margin: 0,
            }}
          >
            {copy.dek}
          </p>
        </div>
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          data-testid="profile-save-btn"
          className="wn-btn wn-btn-accent inline-flex items-center"
          style={{
            gap: 8,
            opacity: !dirty || saving ? 0.55 : 1,
            cursor: !dirty || saving ? "not-allowed" : "pointer",
          }}
        >
          <Save size={14} />
          {saving ? "Saving…" : dirty ? "Save changes" : "All changes saved"}
        </button>
      </header>

      {/* Identity card */}
      <div
        className="grid lg:grid-cols-[300px_1fr] gap-10"
        style={{
          borderTop: "1px solid #D9D8E4",
          padding: "32px 0",
        }}
      >
        <div className="flex flex-col items-start" style={{ gap: 16 }}>
          <div
            data-testid="avatar-preview"
            style={{
              width: 132,
              height: 132,
              borderRadius: 999,
              background: avatarPreview
                ? `center/cover no-repeat url(${avatarPreview})`
                : "#8F3C23",
              color: "#FBFAFD",
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 44,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #D9D8E4",
              letterSpacing: "0.04em",
            }}
          >
            {!avatarPreview && initials}
          </div>
          <input
            ref={fileRef}
            data-testid="avatar-file-input"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: "none" }}
            onChange={handleAvatarPick}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            data-testid="avatar-upload-btn"
            className="wn-btn wn-btn-secondary inline-flex items-center"
            style={{ gap: 8, fontSize: 12.5 }}
          >
            <ImageUp size={13} />
            Upload photo
          </button>
          <p
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 13,
              lineHeight: 1.55,
              margin: 0,
            }}
          >
            Photos render at 132 px. If no photo is on file, your initials are
            shown.
          </p>
        </div>

        <div className="grid md:grid-cols-2" style={{ gap: 18 }}>
          <Input
            label="Display name"
            testid="profile-name"
            value={draft.name || ""}
            onChange={(v) => update("name", v)}
          />
          <Input
            label="Email"
            testid="profile-email"
            value={draft.email || ""}
            onChange={(v) => update("email", v)}
            type="email"
          />
          <Input
            label="Phone"
            testid="profile-phone"
            value={draft.phone || ""}
            onChange={(v) => update("phone", v)}
            placeholder="(317) 555-0149"
          />
          {isCityOrAdmin ? (
            <Input
              label="Title"
              testid="profile-title"
              value={draft.title || ""}
              onChange={(v) => update("title", v)}
              placeholder={
                effectiveRole === "city_staff"
                  ? "Senior Planner · DMD Land Use"
                  : "Director of Platform Operations"
              }
            />
          ) : (
            <Input
              label="Organisation"
              testid="profile-org"
              value={draft.organization || ""}
              onChange={(v) => update("organization", v)}
              placeholder={
                effectiveRole === "architect"
                  ? "Whitfield + Associates"
                  : "Owner-builder · CDC · family LLC"
              }
            />
          )}
          <Input
            label="Neighborhood"
            testid="profile-neighborhood"
            value={draft.neighborhood || ""}
            onChange={(v) => update("neighborhood", v)}
            placeholder="Martindale-Brightwood"
          />
          <Input
            label="Role"
            testid="profile-role"
            value={copy.roleLabel}
            onChange={() => {}}
            readOnly
            hint="Role changes are handled by platform admin."
          />
          <div className="md:col-span-2">
            <Textarea
              label="Short bio"
              testid="profile-bio"
              value={draft.bio || ""}
              onChange={(v) => update("bio", v)}
              placeholder={copy.bioPlaceholder}
            />
          </div>
        </div>
      </div>

      {/* Notification preferences */}
      <div
        style={{ borderTop: "1px solid #D9D8E4", padding: "32px 0 8px" }}
        data-testid="profile-notif-prefs"
      >
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 28,
              height: 2,
              background: "#B14E2F",
            }}
          />
          <span className="wn-label" style={{ color: "#8F3C23" }}>
            Notification preferences
          </span>
        </div>
        <h2
          className="mt-3"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 500,
            fontSize: 26,
            color: "#1B1F4B",
            margin: 0,
            lineHeight: 1.2,
          }}
        >
          {copy.bell}
        </h2>
        <p
          className="mt-2"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 15,
            maxWidth: 640,
          }}
        >
          {copy.bellDek}
        </p>

        <div
          className="mt-6"
          style={{
            borderTop: "1px solid #D9D8E4",
            borderBottom: "1px solid #D9D8E4",
          }}
        >
          {notifOptions.map(([key, label, body]) => (
            <ToggleRow
              key={key}
              k={key}
              label={label}
              body={body}
              value={!!draft.notification_prefs?.[key]}
              onChange={(v) => updatePref(key, v)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function Input({ label, value, onChange, testid, type = "text",
                 placeholder, readOnly, hint }) {
  return (
    <label style={{ display: "block" }}>
      <span className="wn-label" style={{ color: "#6C6B85" }}>{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        readOnly={readOnly}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{
          display: "block",
          width: "100%",
          marginTop: 6,
          padding: "10px 12px",
          border: "1px solid #D9D8E4",
          borderRadius: 4,
          background: readOnly ? "#F5F4FA" : "#FBFAFD",
          fontFamily: '"Nunito Sans", sans-serif',
          color: readOnly ? "#6C6B85" : "#1B1F4B",
          fontSize: 14,
          outline: "none",
        }}
        onFocus={(e) => !readOnly && (e.target.style.borderColor = "#B14E2F")}
        onBlur={(e) => (e.target.style.borderColor = "#D9D8E4")}
      />
      {hint && (
        <span
          style={{
            display: "block", marginTop: 5,
            color: "#6C6B85", fontSize: 12, fontStyle: "italic",
            fontFamily: '"Newsreader", Georgia, serif',
          }}
        >
          {hint}
        </span>
      )}
    </label>
  );
}

function Textarea({ label, value, onChange, testid, placeholder }) {
  return (
    <label style={{ display: "block" }}>
      <span className="wn-label" style={{ color: "#6C6B85" }}>{label}</span>
      <textarea
        data-testid={testid}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        style={{
          display: "block", width: "100%", marginTop: 6,
          padding: "10px 12px",
          border: "1px solid #D9D8E4", borderRadius: 4, background: "#FBFAFD",
          fontFamily: '"Nunito Sans", sans-serif',
          color: "#1B1F4B", fontSize: 14, outline: "none", resize: "vertical",
        }}
        onFocus={(e) => (e.target.style.borderColor = "#B14E2F")}
        onBlur={(e) => (e.target.style.borderColor = "#D9D8E4")}
      />
    </label>
  );
}

function ToggleRow({ k, label, body, value, onChange }) {
  return (
    <div
      className="grid grid-cols-[1fr_auto] items-center"
      style={{ padding: "18px 4px", borderTop: "1px solid #ECEBF1", gap: 32 }}
      data-testid={`pref-row-${k}`}
    >
      <div>
        <div style={{
          fontFamily: '"Nunito Sans", sans-serif', color: "#1B1F4B",
          fontWeight: 700, fontSize: 15,
        }}>
          {label}
        </div>
        <div style={{
          color: "#272636", fontSize: 13.5, lineHeight: 1.5,
          marginTop: 4, maxWidth: 620,
        }}>
          {body}
        </div>
      </div>
      <button
        type="button" role="switch" aria-checked={value}
        onClick={() => onChange(!value)}
        data-testid={`pref-toggle-${k}`}
        style={{
          width: 44, height: 24, padding: 2,
          borderRadius: 999, border: "1px solid transparent",
          background: value ? "#1B1F4B" : "#D9D8E4",
          cursor: "pointer", display: "inline-flex", alignItems: "center",
          justifyContent: value ? "flex-end" : "flex-start",
          transition: "background-color 180ms ease, justify-content 180ms ease",
        }}
      >
        <span aria-hidden style={{
          display: "block", width: 18, height: 18, borderRadius: 999,
          background: "#FBFAFD", boxShadow: "0 1px 2px rgba(27,31,75,0.20)",
        }} />
      </button>
    </div>
  );
}
